import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NumberUtils, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { IEquityMovement } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs';
import { IEmsPeriodChange, IEmsPeriodColumn } from '../../interfaces/equity-movement-schedule-period-column.interface';

const RECONCILIATION_TOLERANCE = 0.005;
const INITIAL_EMPTY_ROWS = 3;

export type EmsReconciliationCell = { state: 'ok' } | { state: 'off'; amount: number } | { state: 'none' };

@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-equity-movement-schedule-component',
    templateUrl: 'equity-movement-schedule.component.html',
    styleUrls: ['equity-movement-schedule.component.scss'],
})
export class EquityMovementScheduleComponent implements OnChanges {
    readonly numberFormat = '1.2-2';

    @Input() columns: IEmsPeriodColumn[] = [];
    @Input() periodLabel = '';
    @Input() openingTitle = '';
    @Input() closingTitle = '';
    @Input() closingDate: string | undefined = '';

    @Output() periodChange = new EventEmitter<IEmsPeriodChange>();
    @Output() columnRename = new EventEmitter<{ id: string; title: string }>();
    @Output() columnDelete = new EventEmitter<string>();
    @Output() columnToggleSource = new EventEmitter<{ totalId: string; sourceId: string }>();
    @Output() columnReorder = new EventEmitter<{ previousIndex: number; currentIndex: number }>();

    form: UntypedFormGroup | undefined;
    displayTable = false;
    editingColumnId: string | null = null;
    editingOpeningTitle = false;
    editingClosingTitle = false;
    rowPendingDelete: number | null = null;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly domSanitizer: DomSanitizer,
    ) {
        this.addSvgIcon('aumico-pattern-icon-settings-vertical');
    }

    /**
     * Only a changed column set may rebuild the form. An edited row title travels up to the parent and
     * comes straight back down on `openingTitle`/`closingTitle`; rebuilding on that would reset every
     * cell to the last `columns` snapshot, which does not carry the values typed since.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['columns'] == null) {
            return;
        }
        if (this.columns.length === 0) {
            this.displayTable = false;
            return;
        }
        this.buildForm();
        this.displayTable = true;
    }

    private buildForm(): void {
        const rowCount = this.columns[0].movements.length || INITIAL_EMPTY_ROWS;

        const openingControls: { [id: string]: UntypedFormControl } = {};
        this.columns
            .filter((col) => col.openingEditable && col.type !== 'TOTAL')
            .forEach((col) => (openingControls[col.id] = new UntypedFormControl(col.opening)));

        const rows = this.formBuilder.array(
            Array.from({ length: rowCount }, (_unused, rowIndex) => this.buildRow(this.rowNameAt(rowIndex), rowIndex)),
        );

        this.form = this.formBuilder.group({ opening: this.formBuilder.group(openingControls), rows });
        this.form.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe(() => this.emit());
    }

    private buildRow(name: string | null, rowIndex: number): UntypedFormGroup {
        const valueControls: { [id: string]: UntypedFormControl } = {};
        this.columns
            .filter((col) => col.type !== 'TOTAL')
            .forEach((col) => (valueControls[col.id] = new UntypedFormControl(col.movements[rowIndex]?.value ?? null)));
        return this.formBuilder.group({
            name: new UntypedFormControl(name),
            values: this.formBuilder.group(valueControls),
        });
    }

    get rows(): UntypedFormArray {
        return this.form.get('rows') as UntypedFormArray;
    }

    get openingGroup(): UntypedFormGroup {
        return this.form.get('opening') as UntypedFormGroup;
    }

    trackByColumnId(_index: number, col: IEmsPeriodColumn): string {
        return col.id;
    }

    trackBySourceId(_index: number, source: { id: string }): string {
        return source.id;
    }


    selectableColumnsFor(total: IEmsPeriodColumn): { id: string; title: string; selected: boolean }[] {
        if (total.type !== 'TOTAL') {
            return [];
        }
        const selected = total.sumUpTotalColIDs ?? [];
        return this.columns
            .filter((col) => col.type !== 'TOTAL')
            .map((col) => ({ id: col.id, title: col.title, selected: selected.includes(col.id) }));
    }

    private rowNameAt(rowIndex: number): string | null {
        return this.columns[0].movements[rowIndex]?.name ?? null;
    }

    private columnById(id: string): IEmsPeriodColumn | undefined {
        return this.columns.find((col) => col.id === id);
    }

    cellValue(col: IEmsPeriodColumn, rowIndex: number): number | null {
        if (col.type === 'TOTAL') {
            return this.sumSources(col, (source) => this.cellValue(source, rowIndex));
        }
        return this.rows.at(rowIndex).get(['values', col.id])?.value ?? null;
    }

    openingValue(col: IEmsPeriodColumn): number | null {
        if (col.type === 'TOTAL') {
            return this.sumSources(col, (source) => this.openingValue(source));
        }
        if (col.openingEditable) {
            return this.openingGroup.get(col.id)?.value ?? null;
        }
        return col.opening;
    }

    private movementsSum(col: IEmsPeriodColumn): number {
        const values = this.rows.controls.map((_row, rowIndex) => this.cellValue(col, rowIndex));
        return NumberUtils.sumIgnoringInfiniteNumbers(values, 0);
    }

    closingValue(col: IEmsPeriodColumn): number | null {
        if (col.type === 'TOTAL') {
            return this.sumSources(col, (source) => this.closingValue(source));
        }
        if (col.type === 'SYSTEM') {
            return col.closingTarget;
        }
        return NumberUtils.sumIgnoringInfiniteNumbers([this.openingValue(col), this.movementsSum(col)], 0);
    }

    reconciliation(col: IEmsPeriodColumn): EmsReconciliationCell {
        if (col.type === 'USERDEFINED') {
            return { state: 'none' };
        }
        const computed = NumberUtils.sumIgnoringInfiniteNumbers([this.openingValue(col), this.movementsSum(col)], 0);
        const amount = NumberUtils.sumIgnoringInfiniteNumbers([this.closingValue(col), -computed], 0);
        return Math.abs(amount) < RECONCILIATION_TOLERANCE ? { state: 'ok' } : { state: 'off', amount };
    }

    private sumSources(total: IEmsPeriodColumn, valueOf: (source: IEmsPeriodColumn) => number | null): number {
        const sourceIds = total.sumUpTotalColIDs ?? [];
        const values = sourceIds.map((id) => {
            const source = this.columnById(id);
            return source != null ? valueOf(source) : null;
        });
        return NumberUtils.sumIgnoringInfiniteNumbers(values, 0);
    }

    onAddRow(): void {
        this.rows.push(this.buildRow(null, this.rows.length));
    }

    onRemoveRow(rowIndex: number): void {
        this.rowPendingDelete = rowIndex;
    }

    onCancelRowDelete(): void {
        this.rowPendingDelete = null;
    }

    onConfirmRowDelete(): void {
        const rowIndex = this.rowPendingDelete;
        this.rowPendingDelete = null;
        if (rowIndex != null) {
            this.rows.removeAt(rowIndex);
        }
    }

    onRowDrop(event: CdkDragDrop<unknown>): void {
        const control = this.rows.at(event.previousIndex);
        this.rows.removeAt(event.previousIndex);
        this.rows.insert(event.currentIndex, control);
    }

    onOpeningTitleChanged(newTitle: string): void {
        this.openingTitle = newTitle;
        this.emit();
    }

    onClosingTitleChanged(newTitle: string): void {
        this.closingTitle = newTitle;
        this.emit();
    }

    startEditingOpeningTitle(): void {
        this.editingOpeningTitle = true;
    }

    commitOpeningTitle(value: string): void {
        this.editingOpeningTitle = false;
        const trimmed = value.trim();
        if (trimmed.length > 0) {
            this.onOpeningTitleChanged(trimmed);
        }
    }

    startEditingClosingTitle(): void {
        this.editingClosingTitle = true;
    }

    commitClosingTitle(value: string): void {
        this.editingClosingTitle = false;
        const trimmed = value.trim();
        if (trimmed.length > 0) {
            this.onClosingTitleChanged(trimmed);
        }
    }

    startColumnEditing(id: string): void {
        this.editingColumnId = id;
    }

    commitColumnRename(id: string, value: string): void {
        this.editingColumnId = null;
        const trimmed = value.trim();
        const current = this.columns.find((col) => col.id === id);
        if (trimmed.length > 0 && trimmed !== current?.title) {
            this.columnRename.emit({ id, title: trimmed });
        }
    }

    onColumnDelete(id: string): void {
        this.columnDelete.emit(id);
    }

    onColumnToggleSource(totalId: string, sourceId: string): void {
        this.columnToggleSource.emit({ totalId, sourceId });
    }

    onColumnDrop(event: CdkDragDrop<unknown>): void {
        this.columnReorder.emit({ previousIndex: event.previousIndex, currentIndex: event.currentIndex });
    }




    private emit(): void {
        const change: IEmsPeriodChange = {
            openingTitle: this.openingTitle,
            closingTitle: this.closingTitle,
            columns: this.columns.map((col) => ({
                id: col.id,
                opening:
                    col.type === 'TOTAL'
                        ? null
                        : col.openingEditable
                          ? (this.openingGroup.get(col.id)?.value ?? null)
                          : col.opening,
                movements: this.rows.controls.map((row, rowIndex): IEquityMovement => ({
                    name: row.get('name')?.value ?? null,
                    value: col.type === 'TOTAL' ? null : this.cellValue(col, rowIndex),
                })),
            })),
        };
        this.periodChange.emit(change);
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
